import { Box, Button, Heading, Text } from '@squire/ui';

import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type {
  StandbyUnavailableScreenProps,
  StandbyUnavailableVariant,
} from './types';

// `as const` keeps the literal key types the translation function requires.
const TITLE_KEY = {
  generic: 'unavailable.title',
  disabled: 'unavailable.title',
  error: 'unavailable.errorTitle',
} as const satisfies Record<StandbyUnavailableVariant, string>;

const BODY_KEY = {
  generic: 'unavailable.genericBody',
  disabled: 'unavailable.disabledBody',
  error: 'unavailable.errorBody',
} as const satisfies Record<StandbyUnavailableVariant, string>;

// Interim unavailable / error state. SQR-14970 replaces the disabled and
// not-found variants with the designed screen.
const StandbyUnavailableScreen = ({
  variant,
  onRetry,
  isRetrying = false,
}: StandbyUnavailableScreenProps) => {
  const { t } = useScreenTranslations('StandbyRegistrationScreen');

  return (
    <Box style={styles.container} testID="standby-unavailable-screen">
      <Box style={styles.content}>
        <Heading
          variant="m"
          color="textPrimary"
          align="center"
          accessibilityRole="header"
        >
          {t(TITLE_KEY[variant])}
        </Heading>
        <Text variant="m" color="textSecondary" align="center">
          {t(BODY_KEY[variant])}
        </Text>
        {variant === 'error' && onRetry ? (
          <Button
            variant="primary"
            size="large"
            onPress={onRetry}
            loading={isRetrying}
            disabled={isRetrying}
            testID="standby-unavailable-retry"
            accessibilityLabel={t('accessibility.retryButton')}
            accessibilityHint={t('accessibility.retryButtonHint')}
            aria-disabled={isRetrying || undefined}
          >
            {t('unavailable.retry')}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

StandbyUnavailableScreen.displayName = 'StandbyUnavailableScreen';
export { StandbyUnavailableScreen };
